import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Shield, Eye, EyeOff, AlertTriangle, Clock, ArrowLeft, KeyRound } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const { completeAdminLogin } = useAuth();
  const navigate = useNavigate();

  // 2FA state
  const [needs2fa, setNeeds2fa] = useState(false);
  const [partialToken, setPartialToken] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [isBackupCode, setIsBackupCode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data.requires_2fa) {
        setPartialToken(res.data.partial_token);
        setNeeds2fa(true);
        setLoading(false);
        return;
      }
      completeAdminLogin(res.data);
      toast.success("Connexion reussie");
      navigate("/admin");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Erreur de connexion";
      if (status === 429) {
        setError({ type: "lockout", message: detail });
      } else if (status === 401) {
        setError({ type: "credentials", message: "Courriel ou mot de passe incorrect. Verifiez vos identifiants." });
      } else {
        setError({ type: "generic", message: detail });
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2faVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post("/auth/2fa/verify", { partial_token: partialToken, totp_code: totpCode });
      completeAdminLogin(res.data);
      toast.success("Connexion reussie");
      navigate("/admin");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Code invalide";
      if (status === 429) {
        setError({ type: "2fa", message: detail });
      } else if (isBackupCode) {
        setError({ type: "2fa", message: "Code de secours invalide. Verifiez la casse et les espaces." });
      } else {
        setError({ type: "2fa", message: "Code 2FA invalide. Assurez-vous que le code est encore valide (change chaque 30 secondes)." });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#072354] to-[#2877D1] px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-[#072354] to-[#2877D1] px-6 py-7 text-white text-center">
          <div className="flex items-center justify-center gap-3 mb-1">
            <img src="/dg-logo.png" alt="DG Fiscal Flow" className="w-11 h-11 object-contain" />
            <div className="text-left">
              <h1 className="text-xl font-bold tracking-tight">DG Fiscal Flow</h1>
              <p className="text-xs text-blue-200">Espace gestion</p>
            </div>
          </div>
        </div>

        {needs2fa ? (
          <form onSubmit={handle2faVerify} className="p-6 space-y-4" data-testid="2fa-form">
            <div className="text-center mb-2">
              <Shield className="w-10 h-10 text-blue-600 mx-auto mb-2" />
              <h2 className="text-lg font-semibold text-gray-800">Verification en deux etapes</h2>
              <p className="text-sm text-gray-500 mt-1">
                {isBackupCode
                  ? "Entrez un de vos codes de secours"
                  : "Ouvrez votre application d'authentification et entrez le code a 6 chiffres"}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="2fa-error">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error.message}</p>
              </div>
            )}

            {isBackupCode ? (
              <input type="text" value={totpCode} onChange={e => setTotpCode(e.target.value.toUpperCase())}
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-center text-lg font-mono tracking-widest focus:border-blue-500 focus:ring-0"
                placeholder="CODE DE SECOURS" autoFocus data-testid="2fa-backup-input" />
            ) : (
              <input type="text" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] focus:border-blue-500 focus:ring-0"
                placeholder="000000" maxLength={6} autoFocus data-testid="2fa-login-code" />
            )}

            <button type="button" onClick={() => { setIsBackupCode(!isBackupCode); setTotpCode(""); setError(null); }}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto" data-testid="toggle-backup-code">
              <KeyRound className="w-3 h-3" />
              {isBackupCode ? "Utiliser le code de l'application" : "Utiliser un code de secours"}
            </button>

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => { setNeeds2fa(false); setTotpCode(""); setError(null); setIsBackupCode(false); }}
                className="flex-1 border border-gray-300 text-gray-600 px-4 py-2.5 rounded-lg text-sm hover:bg-gray-50 flex items-center justify-center gap-1.5" data-testid="2fa-back-btn">
                <ArrowLeft className="w-3.5 h-3.5" /> Retour
              </button>
              <button type="submit" disabled={loading || (!isBackupCode && totpCode.length < 6) || (isBackupCode && totpCode.length < 4)}
                className="flex-1 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 font-medium"
                data-testid="2fa-verify-btn">
                {loading ? "..." : "Verifier"}
              </button>
            </div>

            <div className="text-center pt-2 border-t">
              <p className="text-xs text-gray-400 mt-2">
                Probleme d'acces? Contactez votre administrateur pour regenerer vos codes de secours depuis Parametres &gt; Compte &gt; 2FA.
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="admin-login-form">
            <h2 className="text-base font-semibold text-gray-700 text-center">Connexion administrateur</h2>

            {error && (
              <div className={`rounded-lg p-3 flex items-start gap-2 ${
                error.type === "lockout" ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"
              }`} data-testid="login-error">
                {error.type === "lockout" ? (
                  <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className={`text-sm ${error.type === "lockout" ? "text-amber-700" : "text-red-700"}`}>{error.message}</p>
                  {error.type === "lockout" && (
                    <p className="text-xs text-amber-600 mt-1">Il s'agit d'une mesure de protection automatique. Votre compte sera debloque automatiquement.</p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identifiant ou courriel</label>
              <input type="text" value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 text-sm"
                placeholder="Votre identifiant" data-testid="login-email-input" autoFocus />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => { setPassword(e.target.value); setError(null); }}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 pr-10 text-sm"
                  placeholder="Votre mot de passe" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading || !email || !password}
              className="w-full bg-[#072354] text-white py-2.5 rounded-lg font-medium disabled:opacity-50 hover:bg-[#0a2d6e] transition text-sm"
              data-testid="login-submit-btn">
              {loading ? "Connexion..." : "Se connecter"}
            </button>

            <div className="flex justify-center pt-2 border-t">
              <Link to="/" className="text-sm text-gray-500 hover:underline">Page d'accueil</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
